/**
 * @fileoverview This file defines the `ResetPassword` React component, which allows users to request a password reset email. It utilizes the `useAuth` hook to call the `resetPassword` function and manages the form submission, loading state, success message, and error handling.
 */

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import '../../styles/components/PasswordReset.css'

export const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const { resetPassword } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await resetPassword(email)
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="password-reset-card">
          <div className="password-reset-success">
            <h2 className="password-reset-title">Check your email</h2>
            <p>We've sent a password reset link to {email}</p>
            <p>Follow the instructions in the email to reset your password.</p>
            <div className="password-reset-back-link">
              <Link to="/login" className="link">
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="password-reset-card">
        <Link to="/" className="home-link">
          Ref.
        </Link>
        <h2 className="password-reset-title">Reset your password</h2>
        <p className="password-reset-subtitle">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        <form className="password-reset-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="cta-button primary password-reset-submit"
          >
            {loading ? 'Sending...' : 'Send reset email'}
          </button>

          <div className="password-reset-back-link">
            <Link to="/signin" className="link">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
